/**
 * File Attachment Rules (FAR) — client-side evaluation engine.
 *
 * Pure functions only (no React, no API calls) so the same logic can be unit
 * tested and mirrored on the backend. The backend MUST re-evaluate on send;
 * this engine only drives the real-time UI on the Send page.
 *
 * Rule shape (normalized):
 *   { id, name, description, active, prefix, prefixOp, suffix, suffixOp,
 *     directory, sendlinxFolder, emails[], to, cc, bcc, createdAt, updatedAt, updatedBy }
 *
 * Operators (legacy Outlook values): 0 = None (treated as AND), 1 = AND, 2 = OR.
 * Expression order: prefix <prefixOp> suffix <suffixOp> folder, evaluated
 * left to right, skipping empty conditions.
 *
 * Folder conditions:
 *   - directory: a LOCAL path (C:\...). A browser cannot see local paths, so it is
 *     checked by the Outlook plug-in only and dropped from the web expression.
 *   - sendlinxFolder: a Sendlinx My Docs folder ("/My Docs/Customers/ABC"). The
 *     web checks it for files sent from My Docs, including subfolders. Uploaded
 *     files have no Sendlinx folder, so the condition is false for them.
 *   On the web the Sendlinx folder takes the directory's place in the expression.
 *
 * To/CC/BCC (inspect fields) do not apply on the web: the portal has one recipient
 * list, so every active rule is checked. The flags are kept for the Outlook plug-in.
 *
 * Recipient exclusions (FAR Recipient Exclusions addendum v2): a rule may also list
 * blocked email addresses and blocked domains. When a file matches the rule, a blocked
 * recipient is EXCLUDED even if it is also authorized (exclusion wins). Domains compare
 * the part after the last "@" exactly (competitor.com does not match notcompetitor.com
 * or mail.competitor.com). The rule's exclusionAction decides what can happen next:
 *   block          no override at all (remove the recipient)
 *   admin_approval only an administrator can authorize, with a reason
 *   global_policy  the normal FAR Settings exception flow
 * Several matching rules: the strictest action wins (block > admin_approval > global_policy).
 * The Send page receives sha256 hashes of the blocked entries (blockedEmailHashes /
 * blockedDomainHashes); rule editors receive the plain lists. Mirrors FarEngine.php.
 */

export const FAR_EXTENSION_KEY = "file_attachment_rules";

export const FAR_OP = { NONE: 0, AND: 1, OR: 2 };

export const FAR_ENFORCEMENT = {
    WARN_REQUIRE_AUTH: "warn_require_auth",
    BLOCK: "block",
    WARN_ONLY: "warn_only"
};

export const FAR_DEFAULT_MESSAGE = 'The file "{file}" matches the File Attachment Rule "{rule}". This file can only be sent to: {recipients}.\nPlease verify the recipients or authorize to continue.';

export const FAR_DEFAULT_SETTINGS = {
    enforcement: FAR_ENFORCEMENT.WARN_REQUIRE_AUTH,
    override_permission: "any",          // "any" | "admins"
    require_override_reason: true,
    log_overrides: true,
    notify_admins: false,
    real_time: true,
    case_insensitive: true,
    match_mode: "all",                   // "all" | "first"
    include_compressed: false,           // Outlook plug-in / server only
    show_status_banner: true,
    show_rule_details: true,
    show_authorized_recipients: true,
    highlight_invalid_recipients: true,
    allow_ignore: true,
    remember_override_reason: false,
    default_message: FAR_DEFAULT_MESSAGE
};

/* File-level states */
export const FAR_STATE = {
    NONE: "none",                 // no rule applies
    MATCHED: "matched",           // rule applies, no recipients entered yet
    AUTHORIZED: "authorized",     // rule applies, all recipients authorized
    UNAUTHORIZED: "unauthorized", // rule applies, no recipient authorized
    MIXED: "mixed",               // rule applies, some recipients not authorized
    LINK: "link",                 // rule applies but transfer is a public link
    OVERRIDDEN: "overridden",     // all problems resolved by an exception
    EXCLUDED: "excluded"          // a recipient is blocked by a rule exclusion
};

export const FAR_LINK_RECIPIENT = "(share link)";

/* What happens to an excluded recipient (strictest first). */
export const FAR_EXCLUSION = {
    BLOCK: "block",
    ADMIN: "admin_approval",
    GLOBAL: "global_policy"
};
const EXCLUSION_ORDER = [FAR_EXCLUSION.BLOCK, FAR_EXCLUSION.ADMIN, FAR_EXCLUSION.GLOBAL];

export const FAR_EXCLUSION_LABEL = {
    [FAR_EXCLUSION.BLOCK]: "Block sending — no sender override",
    [FAR_EXCLUSION.ADMIN]: "Require administrator authorization",
    [FAR_EXCLUSION.GLOBAL]: "Use global FAR exception settings"
};

export const FAR_MAX_BLOCKED_ENTRIES = 200;

const toBool = (value, fallback = false) => {
    if(value === undefined || value === null || value === ""){
        return fallback;
    }
    if(typeof(value) === "string"){
        return value === "1" || value.toLowerCase() === "true";
    }
    return !!value;
};

const toInt = (value, fallback = 0) => {
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? fallback : parsed;
};

const str = (value) => (value === undefined || value === null ? "" : String(value)).trim();

export function normalizeEmails(emails){
    if(!emails){
        return [];
    }
    let list = emails;
    if(typeof(emails) === "string"){
        list = emails.split(/[\s,;]+/);
    }
    if(!Array.isArray(list)){
        return [];
    }
    const seen = {};
    const result = [];
    list.forEach((email) => {
        const clean = str(email).toLowerCase();
        if(clean.length > 0 && !seen[clean]){
            seen[clean] = true;
            result.push(clean);
        }
    });
    return result;
}

/* ------------------------------------------------------------------ */
/* Recipient exclusions                                                */
/* ------------------------------------------------------------------ */

export function normalizeExclusionAction(value){
    const clean = str(value).toLowerCase();
    return EXCLUSION_ORDER.indexOf(clean) >= 0 ? clean : FAR_EXCLUSION.BLOCK;
}

/** The stricter of two exclusion actions (block > admin_approval > global_policy). */
export function stricterExclusion(a, b){
    if(!a){ return b || null; }
    if(!b){ return a; }
    return EXCLUSION_ORDER.indexOf(a) <= EXCLUSION_ORDER.indexOf(b) ? a : b;
}

const EMAIL_PATTERN = /^[^\s@,;<>()[\]"]+@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/;
const DOMAIN_PATTERN = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:[a-z]{2,63}|xn--[a-z0-9-]{2,59})$/;

/** Lower-cased address, or "" when it is not a valid email address. */
export function normalizeBlockedEmail(value){
    const email = str(value).toLowerCase();
    if(email.length <= 0 || email.length > 254 || !EMAIL_PATTERN.test(email)){
        return "";
    }
    return email;
}

/** A bare domain such as "competitor.com" (no "@", http://, path, port or wildcard), or "". */
export function normalizeBlockedDomain(value){
    const domain = str(value).toLowerCase().replace(/\.$/, "");
    if(domain.length <= 0 || domain.length > 253 || !DOMAIN_PATTERN.test(domain)){
        return "";
    }
    return domain;
}

/** Why a typed blocked domain is invalid (for inline errors). */
export function describeBlockedDomainError(value){
    const raw = str(value);
    if(raw.indexOf("@") >= 0){ return "Enter the domain without \"@\", for example competitor.com."; }
    if(/^[a-z]+:\/\//i.test(raw) || raw.indexOf("/") >= 0){ return "Enter the domain only, without http:// or a path."; }
    if(raw.indexOf("*") >= 0){ return "Wildcards are not supported. Enter the domain, for example competitor.com."; }
    return "\""+raw+"\" is not a valid domain.";
}

/** Domain of an address: the part after the LAST "@". */
export function emailDomain(email){
    const target = str(email).toLowerCase();
    const at = target.lastIndexOf("@");
    return at < 0 ? "" : target.substring(at + 1);
}

/* Synchronous SHA-256 (hex) of a string's UTF-8 bytes; matches PHP hash('sha256', ...). */
const SHA256_K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
];
const sha256Cache = {};

export function sha256Hex(text){
    const input = String(text);
    if(sha256Cache[input]){
        return sha256Cache[input];
    }
    const utf8 = unescape(encodeURIComponent(input));
    const bytes = [];
    for(let i = 0; i < utf8.length; i++){ bytes.push(utf8.charCodeAt(i)); }
    const bitLength = bytes.length * 8;
    bytes.push(0x80);
    while(bytes.length % 64 !== 56){ bytes.push(0); }
    for(let i = 7; i >= 0; i--){ bytes.push(i >= 4 ? 0 : (bitLength >>> (i * 8)) & 0xff); }
    const h = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
    const w = new Array(64);
    const rotr = (x, n) => (x >>> n) | (x << (32 - n));
    for(let offset = 0; offset < bytes.length; offset += 64){
        for(let i = 0; i < 16; i++){
            const j = offset + i * 4;
            w[i] = ((bytes[j] << 24) | (bytes[j + 1] << 16) | (bytes[j + 2] << 8) | bytes[j + 3]) | 0;
        }
        for(let i = 16; i < 64; i++){
            const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
            const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
            w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0;
        }
        let a = h[0], b = h[1], c = h[2], d = h[3], e = h[4], f = h[5], g = h[6], k = h[7];
        for(let i = 0; i < 64; i++){
            const t1 = (k + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + ((e & f) ^ (~e & g)) + SHA256_K[i] + w[i]) | 0;
            const t2 = ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) | 0;
            k = g; g = f; f = e; e = (d + t1) | 0; d = c; c = b; b = a; a = (t1 + t2) | 0;
        }
        h[0] = (h[0] + a) | 0; h[1] = (h[1] + b) | 0; h[2] = (h[2] + c) | 0; h[3] = (h[3] + d) | 0;
        h[4] = (h[4] + e) | 0; h[5] = (h[5] + f) | 0; h[6] = (h[6] + g) | 0; h[7] = (h[7] + k) | 0;
    }
    const hex = h.map(v => ("00000000"+(v >>> 0).toString(16)).slice(-8)).join("");
    sha256Cache[input] = hex;
    return hex;
}

export function exclusionHash(value){
    return sha256Hex(str(value).toLowerCase());
}

const stringList = (value) => {
    let list = value;
    if(typeof(value) === "string"){
        list = value.split(/[\s,;]+/);
    }
    if(!Array.isArray(list)){
        return [];
    }
    const seen = {};
    const result = [];
    list.forEach(item => {
        const clean = str(item).toLowerCase();
        if(clean.length > 0 && !seen[clean]){
            seen[clean] = true;
            result.push(clean);
        }
    });
    return result;
};

/**
 * Why `email` is excluded by `rule`: { type: "email"|"domain", value } or null.
 * Uses the plain lists when present (rule editors), otherwise the hashed lists.
 */
export function getRuleExclusion(email, rule){
    const target = str(email).toLowerCase();
    if(!rule || target.length <= 0 || target === FAR_LINK_RECIPIENT){
        return null;
    }
    const domain = emailDomain(target);
    if((rule.blockedEmails || []).indexOf(target) >= 0){
        return { type: "email", value: target };
    }
    if(domain && (rule.blockedDomains || []).indexOf(domain) >= 0){
        return { type: "domain", value: domain };
    }
    if((rule.blockedEmailHashes || []).length > 0 && rule.blockedEmailHashes.indexOf(exclusionHash(target)) >= 0){
        return { type: "email", value: target };
    }
    if(domain && (rule.blockedDomainHashes || []).length > 0 && rule.blockedDomainHashes.indexOf(exclusionHash(domain)) >= 0){
        return { type: "domain", value: domain };
    }
    return null;
}

export function ruleHasExclusions(rule){
    return !!rule && (rule.blockedEmailsCount > 0 || rule.blockedDomainsCount > 0);
}

/** "2 emails · 1 domain" | "None" */
export function describeExclusionCounts(rule){
    if(!ruleHasExclusions(rule)){
        return "None";
    }
    const parts = [];
    if(rule.blockedEmailsCount > 0){ parts.push(rule.blockedEmailsCount+" email"+(rule.blockedEmailsCount === 1 ? "" : "s")); }
    if(rule.blockedDomainsCount > 0){ parts.push(rule.blockedDomainsCount+" domain"+(rule.blockedDomainsCount === 1 ? "" : "s")); }
    return parts.join(" · ");
}

/** Accepts the legacy snake_case API row or a camelCase object. */
export function normalizeRule(raw){
    if(!raw){
        return null;
    }
    const pick = (snake, camel) => (raw[camel] !== undefined ? raw[camel] : raw[snake]);
    const rule = {
        id: raw.id,
        name: str(raw.name),
        description: str(raw.description),
        active: toBool(pick("is_active", "active") !== undefined ? pick("is_active", "active") : raw.status, true),
        prefix: str(raw.prefix),
        prefixOp: toInt(pick("prefix_op", "prefixOp"), FAR_OP.NONE),
        suffix: str(raw.suffix),
        suffixOp: toInt(pick("suffix_op", "suffixOp"), FAR_OP.NONE),
        directory: str(raw.directory),
        sendlinxFolder: normalizeFolder(pick("sendlinx_folder", "sendlinxFolder")),
        emails: normalizeEmails(raw.emails),
        to: toBool(raw.to, true),
        cc: toBool(raw.cc, false),
        bcc: toBool(raw.bcc, false),
        createdAt: pick("created_at", "createdAt") || null,
        updatedAt: pick("updated_at", "updatedAt") || pick("created_at", "createdAt") || null,
        updatedBy: pick("updated_by_name", "updatedBy") || pick("created_by_name", "createdBy") || "",
        blockedEmails: stringList(pick("blocked_emails", "blockedEmails")),
        blockedDomains: stringList(pick("blocked_domains", "blockedDomains")),
        blockedEmailHashes: stringList(pick("blocked_email_hashes", "blockedEmailHashes")),
        blockedDomainHashes: stringList(pick("blocked_domain_hashes", "blockedDomainHashes")),
        exclusionAction: normalizeExclusionAction(pick("exclusion_action", "exclusionAction"))
    };
    // Counts come from the API (the Send page gets hashes, not the lists).
    const emailCount = toInt(pick("blocked_emails_count", "blockedEmailsCount"), -1);
    const domainCount = toInt(pick("blocked_domains_count", "blockedDomainsCount"), -1);
    rule.blockedEmailsCount = emailCount >= 0 ? emailCount : Math.max(rule.blockedEmails.length, rule.blockedEmailHashes.length);
    rule.blockedDomainsCount = domainCount >= 0 ? domainCount : Math.max(rule.blockedDomains.length, rule.blockedDomainHashes.length);
    // The plain lists are only present when the user may edit the rule.
    rule.blockedListsVisible = pick("blocked_emails", "blockedEmails") !== undefined || pick("blocked_domains", "blockedDomains") !== undefined;
    if(rule.name.length <= 0){
        rule.name = getRuleAutoName(rule);
    }
    return rule;
}

export function getRuleAutoName(rule){
    const parts = [];
    if(rule.prefix){ parts.push("Prefix: "+rule.prefix); }
    if(rule.suffix){ parts.push("Suffix: "+rule.suffix); }
    if(rule.directory){ parts.push("Directory: "+rule.directory); }
    if(rule.sendlinxFolder){ parts.push("Sendlinx folder: "+rule.sendlinxFolder); }
    if(parts.length <= 0){
        return rule.id ? "Rule #"+rule.id : "Untitled rule";
    }
    return parts.join(" · ");
}

/** "prefix" | "suffix" | "folder" | "directory" | "combined" | "empty" */
export function getRuleType(rule){
    const count = (rule.prefix ? 1 : 0) + (rule.suffix ? 1 : 0) + (rule.directory ? 1 : 0) + (rule.sendlinxFolder ? 1 : 0);
    if(count === 0){ return "empty"; }
    if(count > 1){ return "combined"; }
    if(rule.prefix){ return "prefix"; }
    if(rule.suffix){ return "suffix"; }
    if(rule.sendlinxFolder){ return "folder"; }
    return "directory";
}

/** A rule is checked on the web when it has a prefix, suffix or Sendlinx folder condition. */
export function isRuleWebEvaluable(rule){
    return !!(rule.prefix || rule.suffix || rule.sendlinxFolder);
}

/** To/CC/BCC do not apply on the web (one recipient list), so every rule inspects web recipients. */
export function ruleInspectsWebRecipients(){
    return true;
}

/**
 * Normalizes a Sendlinx folder path: forward slashes, one leading slash, no
 * trailing slash, no empty or "." segments. Returns "" for the root or when the
 * path contains "..".
 */
export function normalizeFolder(path){
    const parts = [];
    const segments = str(path).replace(/\\/g, "/").split("/");
    for(let i = 0; i < segments.length; i++){
        const part = segments[i].trim();
        if(part === "" || part === "."){
            continue;
        }
        if(part === ".."){
            return "";
        }
        parts.push(part);
    }
    return parts.length > 0 ? "/"+parts.join("/") : "";
}

/** True when fileFolder is ruleFolder or one of its subfolders. */
export function matchesFolder(fileFolder, ruleFolder, caseInsensitive = true){
    let rule = normalizeFolder(ruleFolder);
    let file = normalizeFolder(fileFolder);
    if(!rule || !file){
        return false;
    }
    if(caseInsensitive){
        rule = rule.toLowerCase();
        file = file.toLowerCase();
    }
    return file === rule || file.indexOf(rule+"/") === 0;
}

export function getOpLabel(op){
    if(toInt(op) === FAR_OP.OR){ return "OR"; }
    return "AND";
}

export function getBaseName(fileName){
    const name = str(fileName);
    const parts = name.split(/[\\/]/);
    return parts[parts.length - 1];
}

function stripExtension(name){
    const index = name.lastIndexOf(".");
    if(index <= 0){
        return name;
    }
    return name.substring(0, index);
}

export function matchesPrefix(fileName, prefix, caseInsensitive = true){
    if(!prefix){ return false; }
    let name = getBaseName(fileName);
    let value = prefix;
    if(caseInsensitive){
        name = name.toLowerCase();
        value = value.toLowerCase();
    }
    return name.indexOf(value) === 0;
}

/** Suffix matches the end of the name without extension (Inv-637_abc.pdf ~ "_abc") or the full name (".pdf"). */
export function matchesSuffix(fileName, suffix, caseInsensitive = true){
    if(!suffix){ return false; }
    let name = getBaseName(fileName);
    let value = suffix;
    if(caseInsensitive){
        name = name.toLowerCase();
        value = value.toLowerCase();
    }
    const endsWith = (haystack, needle) => haystack.length >= needle.length && haystack.substring(haystack.length - needle.length) === needle;
    return endsWith(stripExtension(name), value) || endsWith(name, value);
}

/**
 * Evaluates a rule's file conditions against a file.
 * `file` is a name string or { name, folder } (folder = Sendlinx folder, or null for uploads).
 * Returns { matched, evaluable, directoryIgnored, conditions: [{type, value, matched}] }
 */
export function evaluateRuleConditions(rule, file, settings = FAR_DEFAULT_SETTINGS){
    const fileName = file && typeof(file) === "object" ? file.name : file;
    const fileFolder = file && typeof(file) === "object" ? file.folder : "";
    const caseInsensitive = settings.case_insensitive !== false;
    const terms = [];
    if(rule.prefix){
        terms.push({ type: "prefix", value: rule.prefix, op: rule.prefixOp, matched: matchesPrefix(fileName, rule.prefix, caseInsensitive) });
    }
    if(rule.suffix){
        terms.push({ type: "suffix", value: rule.suffix, op: rule.suffixOp, matched: matchesSuffix(fileName, rule.suffix, caseInsensitive) });
    }
    if(rule.sendlinxFolder){
        terms.push({ type: "folder", value: rule.sendlinxFolder, op: FAR_OP.NONE, matched: matchesFolder(fileFolder, rule.sendlinxFolder, caseInsensitive) });
    }
    const directoryIgnored = !!rule.directory;
    if(terms.length <= 0){
        return { matched: false, evaluable: false, directoryIgnored, conditions: [] };
    }
    let result = terms[0].matched;
    for(let i = 1; i < terms.length; i++){
        const op = toInt(terms[i - 1].op);
        if(op === FAR_OP.OR){
            result = result || terms[i].matched;
        }else{
            result = result && terms[i].matched;
        }
    }
    return {
        matched: result,
        evaluable: true,
        directoryIgnored,
        conditions: terms.map(t => ({ type: t.type, value: t.value, matched: t.matched }))
    };
}

/** Exact email match, or a domain entry written as "@domain.com" / "*@domain.com". */
export function isRecipientAuthorized(email, authorizedEmails){
    const target = str(email).toLowerCase();
    if(target.length <= 0){
        return false;
    }
    const domain = target.substring(target.lastIndexOf("@"));
    return (authorizedEmails || []).some(entry => {
        const value = str(entry).toLowerCase();
        if(value === target){
            return true;
        }
        if(value.indexOf("*@") === 0){
            return domain === value.substring(1);
        }
        if(value.indexOf("@") === 0){
            return domain === value;
        }
        return false;
    });
}

/**
 * Flattens the Send page file list into individual files.
 * Upload items: {type:"file",file} | {type:"folder",name,files:[File]}.
 * My Docs items (source "mydocs"): {source:"mydocs", type:"file"|"folder", id, name, files:[{id,name,size,folder,path}]}.
 */
export function flattenSendItems(items){
    const result = [];
    (items || []).forEach((item, index) => {
        if(!item){
            return;
        }
        if(item.source === "mydocs"){
            (item.files || []).forEach((file) => {
                result.push({
                    itemKey: "mydocs:"+item.id,
                    itemIndex: index,
                    name: file.name,
                    path: file.path || file.name,
                    folder: normalizeFolder(file.folder) || null,
                    size: file.size || 0
                });
            });
            return;
        }
        if(item.type === "folder"){
            (item.files || []).forEach((file) => {
                result.push({
                    itemKey: "folder:"+item.name,
                    itemIndex: index,
                    name: file.name,
                    path: file.webkitRelativePath || file.name,
                    folder: null,
                    size: file.size
                });
            });
        }else if(item.file){
            result.push({
                itemKey: "file:"+item.file.name,
                itemIndex: index,
                name: item.file.name,
                path: item.file.name,
                folder: null,
                size: item.file.size
            });
        }
    });
    return result;
}

export function getExceptionKey(fileName, ruleId, recipient){
    return [str(fileName).toLowerCase(), String(ruleId), str(recipient).toLowerCase()].join("::");
}

/**
 * Evaluates every file × recipient pair.
 *
 * @param {Object} params
 * @param {Array}  params.items       Send page file list (or pre-flattened with flat: true)
 * @param {Array}  params.recipients  email strings
 * @param {Array}  params.rules       normalized rules (inactive rules are skipped)
 * @param {Object} params.settings    FAR settings (merged with defaults)
 * @param {String} params.transferType "email" | "link"
 * @param {Object} params.exceptions  map of getExceptionKey() => { action, reason }
 * @param {Boolean} params.isAdmin    the sender is an organization administrator
 * @param {Boolean} params.canOverride the sender may authorize normal exceptions (FAR Settings)
 *
 * Each pair also carries: excluded, exclusionType ("email"|"domain"), exclusionValue,
 * exclusionAction (this rule's), effectiveExclusionAction (strictest for this file +
 * recipient) and overridable (whether this sender can resolve it with an exception).
 * An exception on a pair that cannot be overridden is ignored (the server refuses it too).
 */
export function evaluateSend({ items = [], recipients = [], rules = [], settings = {}, transferType = "email", exceptions = {}, isAdmin = false, canOverride = true }){
    const config = Object.assign({}, FAR_DEFAULT_SETTINGS, settings || {});
    const activeRules = (rules || []).filter(rule => rule && rule.active && isRuleWebEvaluable(rule) && ruleInspectsWebRecipients(rule));
    const isLink = transferType === "link";
    const cleanRecipients = normalizeEmails(recipients);
    const files = flattenSendItems(items);

    const fileResults = files.map((file) => {
        let matches = [];
        for(let i = 0; i < activeRules.length; i++){
            const rule = activeRules[i];
            const evaluation = evaluateRuleConditions(rule, file, config);
            if(evaluation.matched){
                matches.push({ rule, evaluation });
                if(config.match_mode === "first"){
                    break;
                }
            }
        }

        if(matches.length <= 0){
            return Object.assign({}, file, { state: FAR_STATE.NONE, matches: [], pairs: [], allowedRecipients: [] });
        }

        // Recipients allowed by every matched rule ("enforce all") or the first rule.
        let allowedRecipients = matches[0].rule.emails.slice();
        matches.slice(1).forEach(match => {
            allowedRecipients = allowedRecipients.filter(email => isRecipientAuthorized(email, match.rule.emails));
        });
        // An address that a matched rule blocks is never shown as allowed.
        allowedRecipients = allowedRecipients.filter(email => !matches.some(match => getRuleExclusion(email, match.rule)));

        const pairs = [];
        const targets = isLink ? [FAR_LINK_RECIPIENT] : cleanRecipients;
        targets.forEach((recipient) => {
            const recipientPairs = [];
            let strictest = null;
            matches.forEach(match => {
                const exclusion = isLink ? null : getRuleExclusion(recipient, match.rule);
                if(exclusion){
                    strictest = stricterExclusion(strictest, match.rule.exclusionAction);
                }
                recipientPairs.push({
                    key: getExceptionKey(file.name, match.rule.id, recipient),
                    fileName: file.name,
                    filePath: file.path,
                    itemKey: file.itemKey,
                    itemIndex: file.itemIndex,
                    ruleId: match.rule.id,
                    ruleName: match.rule.name,
                    recipient,
                    // Exclusion wins over the authorized list.
                    authorized: !isLink && !exclusion && isRecipientAuthorized(recipient, match.rule.emails),
                    excluded: !!exclusion,
                    exclusionType: exclusion ? exclusion.type : null,
                    exclusionValue: exclusion ? exclusion.value : null,
                    exclusionAction: exclusion ? match.rule.exclusionAction : null
                });
            });
            recipientPairs.forEach(pair => {
                pair.effectiveExclusionAction = pair.excluded ? strictest : null;
                pair.overridable = isPairOverridable(pair, isAdmin, canOverride);
                const exception = exceptions[pair.key] || null;
                // Exceptions only count where this sender may give one (mirrors FarService::validateException).
                pair.exception = !pair.authorized && exception && pair.overridable
                    && (pair.effectiveExclusionAction !== FAR_EXCLUSION.ADMIN || exception.action === "authorized") ? exception : null;
                pairs.push(pair);
            });
        });

        const unauthorizedRecipients = {};
        const authorizedRecipients = {};
        pairs.forEach(pair => {
            if(pair.authorized){
                authorizedRecipients[pair.recipient] = true;
            }else{
                unauthorizedRecipients[pair.recipient] = true;
            }
        });
        // A recipient is only authorized if no matched rule rejects it.
        Object.keys(unauthorizedRecipients).forEach(r => { delete authorizedRecipients[r]; });

        const unresolved = pairs.filter(p => !p.authorized && !p.exception);
        const unauthorizedCount = Object.keys(unauthorizedRecipients).length;
        const authorizedCount = Object.keys(authorizedRecipients).length;

        let state = FAR_STATE.AUTHORIZED;
        if(targets.length <= 0){
            state = FAR_STATE.MATCHED;
        }else if(unresolved.some(p => p.excluded)){
            state = FAR_STATE.EXCLUDED;
        }else if(unauthorizedCount > 0 && unresolved.length <= 0){
            state = FAR_STATE.OVERRIDDEN;
        }else if(isLink){
            state = FAR_STATE.LINK;
        }else if(unauthorizedCount > 0 && authorizedCount > 0){
            state = FAR_STATE.MIXED;
        }else if(unauthorizedCount > 0){
            state = FAR_STATE.UNAUTHORIZED;
        }

        return Object.assign({}, file, {
            state,
            matches,
            pairs,
            allowedRecipients,
            unauthorizedRecipients: Object.keys(unauthorizedRecipients),
            authorizedRecipients: Object.keys(authorizedRecipients),
            unresolvedPairs: unresolved
        });
    });

    // Group back to the Send page items (a folder aggregates its files).
    const itemResults = (items || []).map((item, index) => {
        const children = fileResults.filter(f => f.itemIndex === index);
        const matched = children.filter(f => f.state !== FAR_STATE.NONE);
        return {
            itemIndex: index,
            isFolder: item && item.type === "folder",
            name: item ? ((item.type === "folder" || item.source === "mydocs") ? item.name : (item.file ? item.file.name : "")) : "",
            state: worstState(matched.map(f => f.state)),
            files: matched,
            totalFiles: children.length
        };
    });

    const allPairs = [];
    fileResults.forEach(f => { (f.pairs || []).forEach(p => allPairs.push(p)); });
    const unresolvedPairs = allPairs.filter(p => !p.authorized && !p.exception);
    const overriddenPairs = allPairs.filter(p => !p.authorized && p.exception);
    const unauthorizedRecipientSet = {};
    unresolvedPairs.forEach(p => { unauthorizedRecipientSet[p.recipient] = true; });
    const authorizedRecipientSet = {};
    allPairs.forEach(p => {
        if(p.authorized && !unauthorizedRecipientSet[p.recipient]){
            authorizedRecipientSet[p.recipient] = true;
        }
    });

    // Excluded recipients (unresolved) with the strictest exclusion reason per recipient.
    const excludedPairs = unresolvedPairs.filter(p => p.excluded);
    const excludedRecipients = {};
    excludedPairs.forEach(p => {
        const current = excludedRecipients[p.recipient];
        if(!current || stricterExclusion(current.action, p.effectiveExclusionAction) !== current.action){
            excludedRecipients[p.recipient] = { type: p.exclusionType, value: p.exclusionValue, ruleId: p.ruleId, ruleName: p.ruleName, action: p.effectiveExclusionAction, overridable: p.overridable };
        }
    });

    const matchedFiles = fileResults.filter(f => f.state !== FAR_STATE.NONE);
    const enforcement = config.enforcement;
    const hasProblems = unresolvedPairs.length > 0;
    // "Warn only" never lets a block / admin-approval exclusion through.
    const strictExclusion = excludedPairs.some(p => p.effectiveExclusionAction !== FAR_EXCLUSION.GLOBAL);

    return {
        settings: config,
        files: fileResults,
        items: itemResults,
        matchedFiles,
        pairs: allPairs,
        unresolvedPairs,
        overriddenPairs,
        unauthorizedRecipients: Object.keys(unauthorizedRecipientSet),
        authorizedRecipients: Object.keys(authorizedRecipientSet),
        state: matchedFiles.length <= 0 ? FAR_STATE.NONE : worstState(matchedFiles.map(f => f.state)),
        hasProblems,
        excludedPairs,
        excludedRecipients,
        hasExclusions: excludedPairs.length > 0,
        // Pairs this sender cannot resolve with an exception (hard blocks, admin approval for members, ...).
        unoverridablePairs: unresolvedPairs.filter(p => !p.overridable),
        canResolveAll: hasProblems && unresolvedPairs.every(p => p.overridable),
        // Send is blocked by unresolved pairs unless the org runs in "warn only" mode.
        blocksSend: hasProblems && (enforcement !== FAR_ENFORCEMENT.WARN_ONLY || strictExclusion),
        requiresAcknowledgement: hasProblems && enforcement === FAR_ENFORCEMENT.WARN_ONLY && !strictExclusion
    };
}

/** Whether this sender may resolve the pair with an exception. */
export function isPairOverridable(pair, isAdmin, canOverride){
    if(!pair || pair.authorized){
        return false;
    }
    if(pair.excluded){
        if(pair.effectiveExclusionAction === FAR_EXCLUSION.BLOCK){
            return false;
        }
        if(pair.effectiveExclusionAction === FAR_EXCLUSION.ADMIN){
            return !!isAdmin;
        }
    }
    return !!canOverride;
}

/** One-line reason for an excluded recipient (Send page chips, review modal). */
export function describeExclusion(exclusion){
    if(!exclusion){
        return "";
    }
    const what = exclusion.type === "domain" ? "blocked domain "+exclusion.value : "blocked address";
    return "Blocked by FAR exclusion: "+what+(exclusion.ruleName ? " (rule \""+exclusion.ruleName+"\")" : "")+".";
}

const STATE_SEVERITY = {
    [FAR_STATE.NONE]: 0,
    [FAR_STATE.MATCHED]: 1,
    [FAR_STATE.AUTHORIZED]: 2,
    [FAR_STATE.OVERRIDDEN]: 3,
    [FAR_STATE.MIXED]: 4,
    [FAR_STATE.LINK]: 5,
    [FAR_STATE.UNAUTHORIZED]: 6,
    [FAR_STATE.EXCLUDED]: 7
};

export function worstState(states){
    let worst = FAR_STATE.NONE;
    (states || []).forEach(state => {
        if((STATE_SEVERITY[state] || 0) > (STATE_SEVERITY[worst] || 0)){
            worst = state;
        }
    });
    return worst;
}

/** Fills {file}, {rule} and {recipients} placeholders. Output is plain text (render as text, never HTML). */
export function formatFarMessage(template, values = {}){
    const source = str(template).length > 0 ? template : FAR_DEFAULT_MESSAGE;
    return source
        .replace(/\{file\}/g, values.file || "")
        .replace(/\{rule\}/g, values.rule || "")
        .replace(/\{recipients\}/g, values.recipients || "");
}

/** Payload sent with the transfer so the backend can re-check and write the audit record. */
export function buildFarSendPayload(evaluation, actions = []){
    if(!evaluation){
        return null;
    }
    return {
        clientEvaluated: true,
        enforcement: evaluation.settings.enforcement,
        matches: evaluation.pairs.map(p => ({
            file: p.fileName,
            path: p.filePath,
            ruleId: p.ruleId,
            recipient: p.recipient,
            field: "to",
            authorized: p.authorized,
            excluded: !!p.excluded,
            exception: p.exception ? { action: p.exception.action, reason: p.exception.reason || "" } : null
        })),
        actions: actions
    };
}
